<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <div class="mb-4">
            <a class="text-primary text-body-2" style="cursor: pointer;" @click="backToList">‹ {{ $t('labelOrder.label.backToList') }}</a>
          </div>
          <v-alert v-if="flash" type="success" density="compact" closable class="mb-4" :text="flash" @click:close="flash = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />

          <template v-if="detail">
            <!-- 申請情報 -->
            <LabelMetaStrip :items="infoItems" :note="detail.reviewNote || ''" :note-label="noteLabel" class="mb-6">
              <template #status>
                <v-chip size="small" label variant="tonal" :color="statusColor(detail.status)">{{ $t('labelOrder.status.' + detail.status) }}</v-chip>
              </template>
              <template #reviewer>
                <template v-if="detail.reviewUserName">
                  {{ detail.reviewUserName }}
                  <div class="text-caption text-medium-emphasis">{{ detail.reviewedDatetime }}</div>
                </template>
                <span v-else class="text-disabled">—</span>
              </template>
            </LabelMetaStrip>

            <!-- 集計（申請 → 確定 と、その差） -->
            <div class="d-flex align-baseline flex-wrap mb-2" style="gap: 4px 24px;">
              <div>
                <span class="text-body-2 text-medium-emphasis mr-2">{{ $t('labelOrder.label.summary.requestedTotal') }}</span>
                <span class="text-h6">{{ num(sums.requested) }}</span>
                <span class="text-body-2 text-medium-emphasis ml-1">{{ $t('labelOrder.label.unitSheet') }}</span>
              </div>
              <div>
                <span class="text-body-2 text-medium-emphasis mr-2">{{ $t('labelOrder.label.summary.approvedTotal') }}</span>
                <span class="text-h6 text-blue-darken-2">{{ num(sums.approved) }}</span>
                <span class="text-body-2 text-medium-emphasis ml-1">{{ $t('labelOrder.label.unitSheet') }}</span>
              </div>
              <div>
                <span class="text-body-2 text-medium-emphasis mr-2">{{ $t('labelOrder.label.summary.diff') }}</span>
                <span class="text-h6" :class="diffClass(sums.approved - sums.requested)">{{ signed(sums.approved - sums.requested) }}</span>
                <span class="text-body-2 text-medium-emphasis ml-1">{{ $t('labelOrder.label.unitSheet') }}</span>
              </div>
              <div>
                <span class="text-body-2 text-medium-emphasis mr-2">{{ $t('labelOrder.label.summary.changedCount') }}</span>
                <span class="text-h6" :class="sums.changed > 0 ? 'text-amber-darken-3' : ''">{{ sums.changed }}</span>
                <span class="text-body-2 text-medium-emphasis ml-1">{{ $t('labelOrder.label.unitCount') }}</span>
              </div>
            </div>

            <!-- 明細 -->
            <v-table density="compact" class="text-no-wrap">
              <thead>
                <tr>
                  <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                  <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                  <th>{{ $t('labelOrder.label.lineTable.janCode') }}</th>
                  <th class="text-right">{{ $t('labelOrder.label.lineTable.requestedQty') }}</th>
                  <th class="text-right" style="width: 150px;">{{ $t('labelOrder.label.lineTable.approvedQty') }}</th>
                  <th class="text-right">{{ $t('labelOrder.label.lineTable.diff') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in rows" :key="l.siteCode" :class="l.diff !== 0 ? 'bg-amber-lighten-5' : ''">
                  <td class="text-medium-emphasis">{{ l.siteCode }}</td>
                  <td class="font-weight-medium">{{ l.storeName }}</td>
                  <td class="text-medium-emphasis">{{ l.janCode }}</td>
                  <td class="text-right">{{ num(l.requestedQty) }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-if="editing"
                      v-model="editQty[l.siteCode]"
                      type="number"
                      min="0"
                      step="500"
                      variant="underlined"
                      density="compact"
                      hide-details
                      class="text-right-input"
                    />
                    <span v-else-if="l.approvedQty != null" class="font-weight-bold">{{ num(l.approvedQty) }}</span>
                    <span v-else class="text-disabled">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="l.approvedQty == null" class="text-disabled">—</span>
                    <span v-else-if="l.diff === 0" class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.noChange') }}</span>
                    <v-chip v-else size="small" label variant="tonal" :color="l.diff > 0 ? 'blue-darken-1' : 'red-darken-1'">{{ signed(l.diff) }}</v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- 操作（画面下に固定） -->
            <div class="label-footer d-flex align-center">
              <template v-if="!admin">
                <v-chip size="small" label class="mr-3">{{ $t('labelOrder.label.readOnly') }}</v-chip>
                <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.readOnlyNote') }}</span>
              </template>
              <template v-else-if="editing">
                <v-icon color="amber-darken-3" class="mr-2">mdi-pencil-outline</v-icon>
                <span class="text-body-2 text-amber-darken-4">{{ $t('labelOrder.label.editMode') }}</span>
                <v-spacer></v-spacer>
                <v-btn variant="text" class="mr-2" @click="stopEdit">{{ $t('labelOrder.action.stopEdit') }}</v-btn>
                <v-btn class="px-6 bg-amber-darken-2" elevation="0" @click="approveDialog = true">{{ $t('labelOrder.action.doApproveEdited') }}</v-btn>
              </template>
              <template v-else-if="detail.status === STATUS.REQUESTED">
                <v-btn variant="text" color="red-darken-1" @click="openCancel">{{ $t('labelOrder.action.cancel') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn variant="tonal" color="amber-darken-3" class="px-6 mr-3" @click="startEdit">{{ $t('labelOrder.action.approveEdited') }}</v-btn>
                <v-btn class="px-8 bg-green-darken-1" elevation="0" @click="approveDialog = true">{{ $t('labelOrder.action.approve') }}</v-btn>
              </template>
              <span v-else class="text-body-2 text-medium-emphasis">
                {{ $t('labelOrder.label.cannotOperate', [$t('labelOrder.status.' + detail.status)]) }}
              </span>
            </div>
          </template>
        </v-container>

        <LabelApproveDialog
          v-model="approveDialog"
          :request-id="detail?.requestId"
          :edited="editing"
          :lines="editedLines"
          :changed-count="sums.changed"
          :approved-total="sums.approved"
          :line-count="rows.length"
          @approved="onApproved"
        />

        <!-- キャンセル -->
        <v-dialog v-model="cancelDialog" width="560" persistent>
          <v-card>
            <v-card-title>{{ $t('labelOrder.dialog.cancelTitle') }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-body-2 mb-4">{{ $t('labelOrder.dialog.cancelText') }}</div>
              <v-alert v-if="cancelError" type="error" density="compact" class="mb-4" :text="cancelError" />
              <v-textarea
                v-model="cancelReason"
                :label="$t('labelOrder.label.cancelReason') + ' *'"
                :placeholder="$t('labelOrder.label.cancelReasonPlaceholder')"
                variant="outlined"
                density="compact"
                rows="3"
                hide-details="auto"
                counter="500"
              />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="isSubmitting" @click="cancelDialog = false">{{ $t('labelOrder.action.back') }}</v-btn>
              <v-btn class="px-6 bg-red-darken-1" elevation="2" :loading="isSubmitting" @click="doCancel">{{ $t('labelOrder.action.doCancel') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import LabelApproveDialog from "@/components/label/LabelApproveDialog";
import LabelMetaStrip from "@/components/label/LabelMetaStrip";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import { ORDER_STATUS, statusColor, num, signed, isAdmin } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView, LabelApproveDialog, LabelMetaStrip },
  data: () => ({
    STATUS: ORDER_STATUS,
    admin: false,
    detail: null,
    isLoading: false,
    flash: "",
    errorMessage: "",
    // 修正して承認
    editing: false,
    editQty: {},
    approveDialog: false,
    // キャンセル
    cancelDialog: false,
    cancelReason: "",
    cancelError: "",
    isSubmitting: false,
  }),
  computed: {
    infoItems() {
      const d = this.detail;
      const t = (k) => this.$t("labelOrder.label." + k);
      return [
        { label: t("requestId"), value: d.requestId, strong: true },
        { label: t("requestDate"), value: d.requestDate },
        { label: t("requestUser"), value: d.requestUserName || d.requestUserId },
        { label: t("status"), slot: "status" },
        d.deliveryDate
          ? { label: t("deliveryDate"), value: d.deliveryDate, strong: true }
          : { label: t("deliveryDate"), value: t("notSet"), muted: true },
        { label: t("reviewer"), slot: "reviewer" },
      ];
    },
    noteLabel() {
      return this.detail?.status === ORDER_STATUS.CANCELED
        ? this.$t("labelOrder.label.cancelReason")
        : this.$t("labelOrder.label.reviewNote");
    },
    // 表示用の明細。修正中は入力中の値を確定枚数として差分を出す
    rows() {
      const lines = this.detail?.lines ?? [];
      return lines.map((l) => {
        const approvedQty = this.editing ? Number(this.editQty[l.siteCode] ?? l.requestedQty) : l.approvedQty;
        const diff = approvedQty == null ? 0 : approvedQty - l.requestedQty;
        return { ...l, approvedQty, diff };
      });
    },
    sums() {
      // 承認前は申請枚数を確定見込みとして数える。キャンセルは 0
      const canceled = this.detail?.status === ORDER_STATUS.CANCELED;
      return this.rows.reduce(
        (a, l) => ({
          requested: a.requested + l.requestedQty,
          approved: a.approved + (l.approvedQty ?? (canceled ? 0 : l.requestedQty)),
          changed: a.changed + (l.diff !== 0 ? 1 : 0),
        }),
        { requested: 0, approved: 0, changed: 0 }
      );
    },
    editedLines() {
      return this.rows.map((l) => ({ siteCode: l.siteCode, qty: l.approvedQty }));
    },
  },
  mounted() {
    this.admin = isAdmin();
    this.load();
  },
  methods: {
    num,
    signed,
    statusColor,
    diffClass(v) {
      if (v === 0) return "text-medium-emphasis";
      return v > 0 ? "text-blue-darken-2" : "text-red-darken-1";
    },
    load() {
      const requestId = this.$route.query.requestId;
      if (!requestId) {
        this.backToList();
        return;
      }
      this.isLoading = true;
      api.post("/label-order/detail", { requestId })
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            this.detail = null;
            return;
          }
          this.detail = response.data.results;
        })
        .catch(() => {
          this.detail = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startEdit() {
      this.editQty = {};
      (this.detail?.lines ?? []).forEach((l) => (this.editQty[l.siteCode] = l.requestedQty));
      this.editing = true;
    },
    stopEdit() {
      this.editing = false;
      this.editQty = {};
    },
    onApproved(deliveryDate) {
      this.flash = this.$t("labelOrder.message.approved", [this.detail.requestId, deliveryDate]);
      this.stopEdit();
      this.load();
    },
    openCancel() {
      this.cancelReason = "";
      this.cancelError = "";
      this.cancelDialog = true;
    },
    doCancel() {
      this.isSubmitting = true;
      this.cancelError = "";
      api.post("/label-order/cancel", { requestId: this.detail.requestId, reviewNote: this.cancelReason })
        .then((response) => {
          if (response.data?.status == 1) {
            this.cancelError = response.data.errorMessage;
            return;
          }
          this.cancelDialog = false;
          this.flash = this.$t("labelOrder.message.canceled");
          this.load();
        })
        .catch(() => {
          this.cancelError = this.$t("common.message.error");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    backToList() {
      router.push({ name: Paths.LABEL_ORDER });
    },
  },
};
</script>

<style scoped>
.text-right-input :deep(input) {
  text-align: right;
}
.label-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: 16px;
  padding: 12px 4px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
